// Read only the rendered print artifact, never the previous editor's implementation.
// This adapter translates its measured text/artwork into the studio's independent format.
import { defaultStyles, element, uid, contentIndex } from "./studioModel";
const normalize = (text) =>
  String(text || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
const px = (value) => parseFloat(value) || 0;
const hex = (color) => {
  if (/^#[0-9a-f]{6}$/i.test(color)) return color;
  const parts = color.match(/[\d.]+/g);
  return parts
    ? `#${parts
        .slice(0, 3)
        .map((n) => Math.round(+n).toString(16).padStart(2, "0"))
        .join("")}`
    : "#111111";
};
async function imageData(img) {
  await img.decode().catch(() => {});
  if (!img.naturalWidth)
    throw new Error(
      "An image in the original PDF could not load. Please retry."
    );
  const c = document.createElement("canvas"),
    rect = img.getBoundingClientRect();
  const ratio = Math.min(3.125, 2400 / Math.max(rect.width, rect.height));
  c.width = Math.max(1, Math.round(rect.width * ratio));
  c.height = Math.max(1, Math.round(rect.height * ratio));
  const ctx = c.getContext("2d"),
    style = getComputedStyle(img),
    matrix = new DOMMatrix(style.transform);
  const w = img.offsetWidth,
    h = img.offsetHeight;
  // Baking the already-rendered transforms retains mirrored corners and flourishes.
  ctx.translate(c.width / 2, c.height / 2);
  ctx.scale(ratio, ratio);
  ctx.transform(matrix.a, matrix.b, matrix.c, matrix.d, 0, 0);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  return c.toDataURL("image/png");
}
async function svgData(svg) {
  const box = svg.getBoundingClientRect(),
    copy = svg.cloneNode(true),
    css = getComputedStyle(svg);
  copy.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  copy.setAttribute("width", box.width);
  copy.setAttribute("height", box.height);
  copy.style.cssText = `width:${box.width}px;height:${box.height}px;fill:${css.fill};color:${css.color};stroke:${css.stroke}`;
  const url = URL.createObjectURL(
    new Blob([new XMLSerializer().serializeToString(copy)], {
      type: "image/svg+xml",
    })
  );
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(box.width * 4));
    canvas.height = Math.max(1, Math.ceil(box.height * 4));
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}
let fontCache;
async function printFonts() {
  if (fontCache) return fontCache;
  fontCache = (async () => {
    const result = [];
    for (const sheet of Array.from(document.styleSheets)) {
      if (
        !sheet.href ||
        !sheet.href.startsWith("https://fonts.googleapis.com/")
      )
        continue;
      try {
        const css = await (await fetch(sheet.href)).text();
        for (const block of css.match(/@font-face\s*\{[^}]+\}/g) || []) {
          const family = block.match(/font-family:\s*['"]([^'"]+)['"]/i)?.[1];
          if (!family || !/Garamond|Cormorant/i.test(family)) continue;
          if (block.includes("unicode-range") && !/U\+0000-00FF/i.test(block))
            continue;
          const url = block.match(/url\(([^)]+)\)/)?.[1]?.replace(/['"]/g, "");
          if (!url?.startsWith("https://fonts.gstatic.com/")) continue;
          const blob = await (await fetch(url)).blob();
          const src = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve(
                String(reader.result).replace(
                  /^data:[^;]+;/,
                  "data:font/woff2;"
                )
              );
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          result.push({
            family,
            src,
            weight: block.match(/font-weight:\s*([^;]+)/)?.[1] || "400",
            style: block.match(/font-style:\s*([^;]+)/)?.[1] || "normal",
          });
        }
      } catch (_) {
        /* Native print retains installed fonts; embed web fonts when reachable. */
      }
    }
    return result;
  })();
  return fontCache;
}
function collectText(root) {
  const groups = new Map(),
    walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    if (!node.textContent.trim()) continue;
    const parent = node.parentElement;
    if (parent.closest("svg,style,script")) continue;
    const host =
      parent.closest(
        ".mp-item-price,.mp-beer-price,.mp-item-desc,.mp-sec-sub,.mp-beer-name,.mp-item-name,h1,h2,h3,p,li"
      ) || parent;
    if (!groups.has(host)) groups.set(host, []);
    groups.get(host).push(node);
  }
  return groups;
}
function originalRuns(textNodes, originX, originY) {
  const runs = [];
  for (const textNode of textNodes) {
    const marker = document.createElement("span");
    marker.style.cssText =
      "display:inline-block!important;width:0!important;height:0!important;padding:0!important;margin:0!important;border:0!important;vertical-align:baseline!important;";
    textNode.after(marker);
    const full = document.createRange();
    full.selectNodeContents(textNode);
    const lineRects = Array.from(full.getClientRects()).filter(
      (r) => r.height && r.width
    );
    const baselineOffset = lineRects.length
      ? marker.getBoundingClientRect().top - lineRects[lineRects.length - 1].top
      : 0;
    let run = null;
    for (let i = 0; i < textNode.length; i++) {
      const range = document.createRange();
      range.setStart(textNode, i);
      range.setEnd(textNode, i + 1);
      const box = range.getBoundingClientRect();
      if (!box.width && !textNode.textContent[i].trim()) continue;
      const y = (box.top + baselineOffset - originY) * 0.75;
      if (!run || Math.abs(run.y - y) > 0.3) {
        run = { text: "", x: (box.left - originX) * 0.75, y };
        runs.push(run);
      }
      run.text += textNode.textContent[i];
    }
    marker.remove();
  }
  return runs;
}
function roleFor(host) {
  if (host.matches(".mp-beer-separator")) return "note";
  if (host.matches(".mp-bev-title,h1")) return "title";
  if (host.matches(".mp-cover-script")) return "subtitle";
  if (host.matches(".mp-item-price,.mp-beer-price")) return "price";
  if (host.matches(".mp-item-desc,.mp-sec-sub")) return "description";
  if (host.matches(".mp-item-name,.mp-beer-name,h3")) return "dish";
  if (host.matches("h2,h1")) return "section";
  return "note";
}
function identity(host, menus, role, text) {
  if (host.matches(".mp-beer-separator")) return {};
  const binding = host.getAttribute("data-cms-binding");
  if (binding) {
    const [, itemId, field] = binding.split(":");
    const section = Object.values(menus).flatMap((m) => m?.sections || []).find((s) => s.items?.some((i) => i._id === itemId));
    const item = section?.items.find((i) => i._id === itemId);
    if (item) return { binding, itemId, sectionId: section._id,
      sourceText: contentIndex(menus)[binding],
      ...(field === "price" ? { originalPrices: item.price, priceFormat: text.includes("•") ? "bullet" : "decimal" } : {}) };
  }
  const sectionHost = host.closest("section"),
    heading = sectionHost?.querySelector("h2")?.textContent;
  const sections = Object.values(menus).flatMap((m) => m?.sections || []);
  const section = sections.find(
    (s) => normalize(s.header) === normalize(heading)
  );
  const itemHost = host.closest(".mp-item"),
    name = itemHost?.querySelector(".mp-item-name");
  let itemName = name
    ? Array.from(name.childNodes)
        .filter((n) => n.nodeType === 3 || !n.matches?.(".mp-item-price"))
        .map((n) => n.textContent)
        .join("")
    : text;
  const item = (section?.items || sections.flatMap((s) => s.items || [])).find(
    (i) => normalize(i.name) === normalize(itemName)
  );
  const base = { sectionId: section?._id, itemId: item?._id };
  if (role === "section" && section)
    return {
      ...base,
      binding: `section:${section._id}:header`,
      sourceText: section.header,
    };
  if (role === "dish" && item)
    return { ...base, binding: `item:${item._id}:name`, sourceText: item.name };
  if (role === "description" && item)
    return {
      ...base,
      binding: `item:${item._id}:description`,
      sourceText: item.description,
    };
  // Price and special-composite copy are linked by source identity while retaining original formatting.
  if (role === "price" && item)
    return {
      ...base,
      binding: `item:${item._id}:price`,
      priceFormat: text.includes("•") ? "bullet" : "decimal",
      originalPrices: item.price,
      sourceText: null,
    };
  return base;
}
export async function importPrintedMenu(root, menus) {
  if (!root)
    throw new Error(
      "The existing PDF menu is still loading. Please try again."
    );
  await document.fonts.ready;
  const artifact = root.cloneNode(true);
  artifact.style.cssText +=
    ";position:fixed!important;left:-30000px!important;top:0!important;display:block!important;visibility:hidden!important;transform:none!important;width:max-content!important;max-width:none!important;";
  document.body.appendChild(artifact);
  try {
    await Promise.all(
      Array.from(artifact.querySelectorAll("img")).map((img) =>
        img.decode().catch(() => {})
      )
    );
    const orderedPages = [];
    const documents = Object.fromEntries(
      ["dinner", "lunch", "wine"].map((menuKey) => [
        menuKey,
        {
          version: 1,
          name: `Trattoria Demi · ${
            menuKey === "wine"
              ? "Beverages"
              : menuKey.charAt(0).toUpperCase() + menuKey.slice(1)
          }`,
          menuKey,
          source: "original-pdf",
          originalImportCompleted: true,
          paperImportVersion: 2,
          artifactImportVersion: 4,
          themeKey: "original",
          styles: defaultStyles(),
          pages: [],
          assets: [],
          fonts: [],
        },
      ])
    );
    const pages = Array.from(artifact.querySelectorAll("[data-dnd]")).filter(
      (el) => {
        try {
          return JSON.parse(el.dataset.dnd).type === "page";
        } catch (_) {
          return false;
        }
      }
    );
    if (!pages.length)
      throw new Error("No pages were found in the existing PDF output.");
    // The original PDF uses US Letter. Screen styles may leave the print host unconstrained;
    // establish physical paper dimensions before measuring, independent of viewport or flex sizing.
    for (const pageEl of pages) {
      pageEl.style.setProperty("width", "816px", "important");
      pageEl.style.setProperty("height", "1056px", "important");
      pageEl.style.setProperty("min-width", "816px", "important");
      pageEl.style.setProperty("min-height", "1056px", "important");
      pageEl.style.setProperty("max-width", "816px", "important");
      pageEl.style.setProperty("max-height", "1056px", "important");
      pageEl.style.setProperty("box-sizing", "border-box", "important");
      pageEl.style.setProperty("flex-shrink", "0", "important");
    }
    for (const pageEl of pages) {
      const descriptor = JSON.parse(pageEl.dataset.dnd),
        key = descriptor.menu === "cover" ? "dinner" : descriptor.menu,
        doc = documents[key];
      if (!doc) continue;
      const rect = pageEl.getBoundingClientRect(),
        scale = 0.75;
      if (rect.width < 10 || rect.height < 10)
        throw new Error("The original PDF pages could not be measured.");
      const pageStyle = getComputedStyle(pageEl),
        page = {
          sourceMenu: descriptor.menu,
          id: uid(),
          name:
            descriptor.menu === "cover"
              ? "Cover"
              : `Page ${descriptor.pageIndex + 1}`,
          width: 612,
          height: 792,
          margin: 24,
          background:
            pageStyle.backgroundColor === "rgba(0, 0, 0, 0)"
              ? "#ffffff"
              : hex(pageStyle.backgroundColor),
          elements: [],
        };
      const groups = new Map();
      for (const [host, textNodes] of collectText(pageEl)) {
        const css = getComputedStyle(host);
        if (css.display === "none") continue;
        const raw = textNodes.map((n) => n.textContent).join(""),
          text = raw.replace(/\s+/g, " ").trim();
        if (!text) continue;
        const ranges = textNodes.map((n) => {
          const range = document.createRange();
          range.selectNodeContents(n);
          return range;
        });
        const boxes = ranges
          .flatMap((range) => Array.from(range.getClientRects()))
          .filter((r) => r.width && r.height);
        if (!boxes.length) continue;
        const left = Math.min(...boxes.map((r) => r.left)),
          top = Math.min(...boxes.map((r) => r.top)),
          right = Math.max(...boxes.map((r) => r.right)),
          bottom = Math.max(...boxes.map((r) => r.bottom));
        const hostRect = host.getBoundingClientRect(),
          role = roleFor(host),
          id = identity(host, menus, role, text);
        const section = host.closest("section");
        if (section && !groups.has(section)) groups.set(section, uid());
        const fontSize = px(css.fontSize) * scale,
          lineHeight =
            css.lineHeight === "normal"
              ? 1.15
              : px(css.lineHeight) / px(css.fontSize);
        const style = {
          fontFamily: css.fontFamily,
          fontSize,
          fontWeight: Number(css.fontWeight) || 400,
          fontStyle: css.fontStyle,
          fill: hex(css.color),
          align: ["center", "right"].includes(css.textAlign)
            ? css.textAlign
            : "left",
          lineHeight,
          letterSpacing:
            css.letterSpacing === "normal" ? 0 : px(css.letterSpacing) * scale,
          textTransform: css.textTransform,
          fontVariant: css.fontVariant,
          fontFeatureSettings: css.fontFeatureSettings,
        };
        // A nested price shares the dish's block. Its own ink bounds remain independent.
        const blockWidth =
          role === "price" || host.matches(".mp-beer-name")
            ? right - left
            : Math.max(right - left, hostRect.width);
        const x =
          role === "price" || host.matches(".mp-beer-name")
            ? left
            : hostRect.left;
        const n = element(
          role,
          text,
          (x - rect.left) * scale,
          (top - rect.top) * scale,
          Math.max(1, blockWidth * scale),
          Math.max(fontSize * 1.25, (bottom - top) * scale + 3),
          { ...id, groupId: groups.get(section), style, originalText: text }
        );
        if (id.binding) {
          // Bind updates without changing the case/price convention of the existing PDF.
          n.preserveOriginal = true;
          n.sourceSnapshot = id.sourceText;
          if (role === "price")
            n.sourceSnapshot = JSON.stringify(id.originalPrices);
        }
        n.originalLayout = {
          text,
          w: n.w,
          h: n.h,
          style: { ...style },
          runs: originalRuns(textNodes, x, top),
        };
        n.name = text.slice(0, 60);
        page.elements.push(n);
      }
      for (const img of pageEl.querySelectorAll("img")) {
        const box = img.getBoundingClientRect();
        if (!box.width || !box.height) continue;
        const src = await imageData(img),
          section = img.closest("section");
        page.elements.push(
          element(
            "decoration",
            "",
            (box.left - rect.left) * scale,
            (box.top - rect.top) * scale,
            box.width * scale,
            box.height * scale,
            {
              type: "image",
              name:
                img.alt ||
                (img.className.includes("corner")
                  ? "Original corner ornament"
                  : "Original flourish"),
              src,
              artworkSource: img.currentSrc || img.src,
              artworkTransform: (() => {
                const m = new DOMMatrix(getComputedStyle(img).transform);
                return [m.a, m.b, m.c, m.d];
              })(),
              opacity: Number(getComputedStyle(img).opacity),
              groupId: groups.get(section),
              sourceWidth: Math.round(box.width * 3.125),
              sourceHeight: Math.round(box.height * 3.125),
            }
          )
        );
      }
      for (const svg of pageEl.querySelectorAll("svg")) {
        const box = svg.getBoundingClientRect();
        if (!box.width || !box.height) continue;
        page.elements.push(
          element(
            "decoration",
            "",
            (box.left - rect.left) * scale,
            (box.top - rect.top) * scale,
            box.width * scale,
            box.height * scale,
            {
              type: "image",
              name: svg.getAttribute("aria-label") || "Original vector detail",
              itemId: identity(svg, menus, "decoration", "").itemId,
              src: await svgData(svg),
              groupId: groups.get(svg.closest("section")),
            }
          )
        );
      }
      doc.pages.push(page);
      orderedPages.push(page);
    }
    documents.dinner.pages = orderedPages;
    documents.dinner.name = "Trattoria Demi · Complete menu";
    for (const doc of Object.values(documents)) {
      // Make imported typography the shared baseline, keeping differences as overrides.
      doc.pages = JSON.parse(JSON.stringify(doc.pages));
      for (const role of Object.keys(doc.styles)) {
        const nodes = doc.pages
          .flatMap((p) => p.elements)
          .filter((n) => n.role === role && n.type === "text");
        if (!nodes.length) continue;
        doc.styles[role] = { ...doc.styles[role], ...nodes[0].style };
        nodes.forEach((n) => {
          n.style = Object.fromEntries(
            Object.entries(n.style).filter(
              ([k, v]) => doc.styles[role][k] !== v
            )
          );
        });
      }
    }
    const fonts = await printFonts();
    for (const doc of Object.values(documents)) doc.fonts = fonts;
    return documents;
  } finally {
    artifact.remove();
  }
}
